import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleCheck, CircleX, Info } from 'lucide-react';
import AuthLayout from '@/components/common/AuthLayout';
import {
  GHL_OAUTH_STATUS_STORAGE_KEY,
  hasOAuthParams,
  readGhlCallbackStatus,
  withoutOAuthParams,
} from '@/lib/ghlOAuthCallback';

const readPreviousStatus = () => {
  try {
    return sessionStorage.getItem(GHL_OAUTH_STATUS_STORAGE_KEY);
  } catch {
    return null;
  }
};

const savePreviousStatus = (status: string) => {
  try {
    sessionStorage.setItem(GHL_OAUTH_STATUS_STORAGE_KEY, status);
  } catch {
    // Sans stockage, une actualisation affichera l'échec : sans conséquence sur la connexion.
  }
};

export default function GhlOAuthCallbackPage() {
  const { t } = useTranslation('integrations', { keyPrefix: 'ghlCallback' });
  const [searchParams, setSearchParams] = useSearchParams();
  const [status] = useState(() => readGhlCallbackStatus(searchParams, readPreviousStatus()));
  const isSuccess = status === 'success';

  useEffect(() => {
    if (!hasOAuthParams(searchParams)) return;
    savePreviousStatus(status);
    setSearchParams(withoutOAuthParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams, status]);

  const iconClassName = 'mr-3 mt-0.5 size-7 shrink-0 self-start sm:size-8';
  const title = (
    <>
      {isSuccess ? (
        <CircleCheck aria-hidden className={`${iconClassName} text-green-500`} />
      ) : (
        <CircleX aria-hidden className={`${iconClassName} text-red-500`} />
      )}
      <span className="text-2xl sm:text-3xl">
        {isSuccess ? t('success.title') : t('error.title')}
      </span>
    </>
  );

  return (
    <AuthLayout title={title} background="gradient">
      {isSuccess ? (
        <div className="space-y-6">
          <p className="text-sm text-secondary-inverse">{t('success.description')}</p>
          <p className="text-base">{t('success.instructions')}</p>
          <p className="flex items-start gap-3 text-sm text-secondary-inverse">
            <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
            <span>{t('success.activation')}</span>
          </p>
          <p className="text-sm text-secondary-inverse">{t('success.close')}</p>
        </div>
      ) : (
        <p className="text-sm text-secondary-inverse">{t('error.message')}</p>
      )}
    </AuthLayout>
  );
}
